import { type Control, useFormState, useWatch } from 'react-hook-form';
import { Stack, Grid, Typography, Divider, Box } from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';

import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';

import { isNil } from '#packages/data-collection/libs/lodash';

export type FloorsDetailsProps = {
  control: Control<EditAuditFormBackgroundData>;
};

export const FloorsDetails = ({ control }: FloorsDetailsProps) => {
  const { errors } = useFormState({ control });

  const {
    general: { grossFloorArea, floors, parkadeLevels, otherFloors } = {},
  } = useWatch({ control });

  const items = [
    {
      label: 'Gross floor area',
      value: grossFloorArea,
      unit: 'm²',
      error: !!errors.general?.grossFloorArea,
    },
    {
      label: 'Above ground',
      value: floors,
      unit: 'fl',
      error: !!errors.general?.floors,
    },
    {
      label: 'Parkade levels',
      value: parkadeLevels,
      unit: 'fl',
      error: !!errors.general?.parkadeLevels,
    },
    {
      label: 'Other floors',
      value: otherFloors,
      unit: 'fl',
      error: !!errors.general?.otherFloors,
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={1}>
        <Typography
          component="div"
          variant="body2"
          color="textSecondary"
          sx={{ mb: 0.5 }}>
          <Grid container alignItems="center">
            <LayersIcon fontSize="inherit" />
            <span>&nbsp;Floors</span>
          </Grid>
        </Typography>
        {items.map(({ label, value, unit, error }) => (
          <Grid key={label} container justifyContent="space-between">
            <Typography component="span" variant="body2">
              {label}:
            </Typography>
            <Typography
              component="span"
              variant="body2"
              color={
                error ? 'error' : isNil(value) ? 'textDisabled' : 'textPrimary'
              }>
              {isNil(value) ? '—' : `${value} ${unit}`}
            </Typography>
          </Grid>
        ))}
        <Divider />
      </Stack>
    </Box>
  );
};
